import React, { FC } from 'react';
import { FormControl, Select, MenuItem, InputLabel, SelectChangeEvent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserType } from 'models/user';
import { EXTERNAL_USER_ROLES } from 'utils/permission';

export enum TechnicianRole {
    TECHNICIAN_USER = 'TECHNICIAN_USER',
    TECHNICIAN_ADMIN = 'TECHNICIAN_ADMIN',
    EXTERNAL_TECHNICIAN = 'EXTERNAL_TECHNICIAN',
    RESTRICTED_EXTERNAL_USER = 'RESTRICTED_EXTERNAL_USER',
    ELEVATED_EXTERNAL_USER = 'ELEVATED_EXTERNAL_USER',
}

export enum DentistRole {
    USER = 'USER',
    DENTIST_USER = 'DENTIST_USER',
    DENTIST_ADMIN = 'DENTIST_ADMIN',
    DENTIST_CLINIC_MANAGER = 'DENTIST_CLINIC_MANAGER',
}

export const ALL_ROLES = '' as const;

export type UserRole = DentistRole | TechnicianRole | typeof ALL_ROLES;

type UserRoleFilterProps = {
    value: UserRole;
    onRoleSelect: (role: UserRole) => void;
    fullWidth?: boolean;
    hasLocalUser: boolean | null;
    userType: UserType;
};

const isDentistRoleValue = (value: UserRole): boolean => value === DentistRole.USER || value.startsWith('DENTIST');

const isTechnicianRoleValue = (value: UserRole): boolean =>
    value.startsWith('TECHNICIAN') || EXTERNAL_USER_ROLES.includes(value);

const getSafeValue = (value: UserRole, userType: UserType): UserRole => {
    if (value === ALL_ROLES) return ALL_ROLES;
    if (userType === 'dentist' && isDentistRoleValue(value)) return value;
    if (userType === 'lab_technician' && isTechnicianRoleValue(value)) return value;
    if (userType === '' && (isDentistRoleValue(value) || isTechnicianRoleValue(value))) return value;
    return ALL_ROLES;
};

const UserRoleFilter: FC<UserRoleFilterProps> = ({
    value,
    onRoleSelect,
    hasLocalUser,
    fullWidth = false,
    userType,
}) => {
    const { t } = useTranslation(['common', 'client-directory']);

    const handleChange = (event: SelectChangeEvent) => {
        onRoleSelect(event.target.value as UserRole);
    };

    const safeValue = getSafeValue(value, userType);

    const allRolesMenuItem = (
        <MenuItem key="ALL" value={ALL_ROLES}>
            {t('common:ALL')}
        </MenuItem>
    );

    const localUserMenuItem = hasLocalUser && (
        <MenuItem key="USER" value="USER">
            {t('common:USER')}
        </MenuItem>
    );

    const dentistMenuItems = [
        <MenuItem key="DENTIST_USER" value="DENTIST_USER">
            {t('common:DENTIST_USER')}
        </MenuItem>,
        <MenuItem key="DENTIST_ADMIN" value="DENTIST_ADMIN">
            {t('common:DENTIST_ADMIN')}
        </MenuItem>,
        <MenuItem key="DENTIST_CLINIC_MANAGER" value="DENTIST_CLINIC_MANAGER">
            {t('common:DENTIST_CLINIC_MANAGER')}
        </MenuItem>,
    ];

    const technicianMenuItems = [
        <MenuItem key="TECHNICIAN_USER" value="TECHNICIAN_USER">
            {t('common:TECHNICIAN_USER')}
        </MenuItem>,
        <MenuItem key="TECHNICIAN_ADMIN" value="TECHNICIAN_ADMIN">
            {t('common:TECHNICIAN_ADMIN')}
        </MenuItem>,
        ...EXTERNAL_USER_ROLES.map((role) => (
            <MenuItem key={role} value={role}>
                {t(`common:${role}`)}
            </MenuItem>
        )),
    ];

    const getMenuItems = () => {
        if (userType === 'dentist') {
            return [allRolesMenuItem, localUserMenuItem, ...dentistMenuItems].filter(Boolean);
        }
        if (userType === 'lab_technician') {
            return [allRolesMenuItem, ...technicianMenuItems];
        }
        return [allRolesMenuItem, ...dentistMenuItems, ...technicianMenuItems];
    };

    return (
        <FormControl margin="dense" fullWidth={fullWidth} sx={{ minWidth: 150 }}>
            <InputLabel id="user-role-label" shrink>
                {t('common:USER_ROLE')}
            </InputLabel>

            <Select
                labelId="user-role-label"
                value={String(safeValue)}
                displayEmpty
                label={t('common:USER_ROLE')}
                onChange={handleChange}
            >
                {getMenuItems()}
            </Select>
        </FormControl>
    );
};

export default UserRoleFilter;
